import { yupResolver } from '@hookform/resolvers/yup';
import { Box, CircularProgress, Grid, Stack } from '@mui/material';
import { useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { ICandidate } from 'wisestep-api';
import {
  ControlledTextArea,
  ControlledTextInput,
  formatDateInDayMonthYearTime,
  Item,
  Label,
  WsButton,
  WsColors,
  WsDatePicker,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { object, number, date, string } from 'yup';

import ActionFooter from '../../../../conversationWidget/components/ActionFooter';

import VMSRegion from './VMSRegion';

const MandatoryVMSFields = ({
  candidate,
  onSubmit,
  isDisabled,
  navigateBack,
}: {
  candidate: ICandidate;
  onSubmit: (data: FieldValues) => void;
  isDisabled: boolean;
  navigateBack: () => void;
}) => {
  const [region, setRegion] = useState<string | null>(null);
  const formOptions = {
    defaultValues: {
      ssnNumber: candidate.ssnNumber,
      availableFrom: candidate.availableFrom,
      comment: candidate.assessment,
      dob: candidate.dob ? formatDateInDayMonthYearTime(candidate.dob) : null,
    },
    resolver: yupResolver(
      object().shape({
        ssnNumber: number().required('Please enter SSN number').typeError('Number is required'),
        availableFrom: date().required('Please enter Available From').typeError('Date is required'),
        comment: string(),
        dob: date().required('Please enter DOB').typeError('Date is required'),
      })
    ),
  };
  const methods = useForm(formOptions);
  const [disable, setDisable] = useState(isDisabled);
  const { handleSubmit } = methods;

  const submitHandler = (data: FieldValues) => {
    setDisable(true);
    onSubmit(data);
  };

  const addRegion = (region) => {
    candidate.vmsRegion = region;
    setRegion(region);
  };

  return (
    <Grid container>
      <FormProvider {...methods}>
        <Box sx={{ width: '90%' }} component="form" onSubmit={handleSubmit(submitHandler)}>
          <Grid item xs={12}>
            <Item sx={{ mb: 0 }}>
              {!disable ? (
                <>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                    Please add Mandatory fields
                  </WsTypography>
                  <Item>
                    <VMSRegion onChange={addRegion} />
                    {region == 'EU' && (
                      <Grid item lg={4.5} mt={2}>
                        <Label>Candidate Date of Birth*</Label>
                        <WsDatePicker name="dob" label="" disableFuture={false} disabled={false} />
                      </Grid>
                    )}
                    {region != null && (
                      <>
                        <ControlledTextInput
                          name="ssnNumber"
                          labelText="SSN Number *"
                          type="number"
                          fullWidth
                          id="candidate_ssnNumber"
                          placeholder="Enter SSN Number"
                          variant="outlined"
                          autoComplete="candidate_ssnNumber"
                          required={true}
                        />
                        <Grid item lg={4.5} mt={2}>
                          <Label>Available From *</Label>
                          <WsDatePicker
                            name="availableFrom"
                            label=""
                            disableFuture={false}
                            disabled={false}
                          />
                        </Grid>
                        <Grid item mt={2}>
                          <ControlledTextArea
                            name="comment"
                            labelText="Any comments for candidate"
                            showMandatorySymbol={false}
                            minRows={7}
                          />
                        </Grid>
                      </>
                    )}
                  </Item>
                </>
              ) : (
                <Box textAlign="center">
                  <CircularProgress disableShrink sx={{ color: WsColors.TheBlues500 }} />
                </Box>
              )}
            </Item>
          </Grid>
          <ActionFooter>
            <Grid item container xs={6} justifyContent="flex-end">
              <Box mr={3}>
                <Stack direction="row" spacing={2}>
                  <WsButton variant="outlined" onClick={navigateBack}>
                    Back
                  </WsButton>
                  <WsButton variant="outlined" type="submit" disabled={disable || region == null}>
                    Save
                  </WsButton>
                </Stack>
              </Box>
            </Grid>
          </ActionFooter>
        </Box>
      </FormProvider>
    </Grid>
  );
};

export default MandatoryVMSFields;
